import { useEffect, useState } from 'react';
import { useAtom } from 'jotai';

import { LOCAL_STORAGE_KEYS } from '@core/constants';

import atomWithImprovedStorage, {
  getItem,
} from '@core/atoms/utils/atomWithImprovedStorage';

export type CardDensity = 'comfortable' | 'compact';

export interface GridConfig {
  normalColumns: number;
  wideColumns: number;
  cardDensity: CardDensity;
}

export const DEFAULT_GRID_CONFIG: GridConfig = {
  normalColumns: 6,
  wideColumns: 10,
  cardDensity: 'comfortable',
};

// density별 카드 최소 폭.
// comfortable은 180px로 낮춰 WideWrapper(max-width 1280) 본문 영역에서 6열이 여유 있게 확보되도록 함.
// (200px이면 6*200+5*8=1240 > 1200 실 본문폭 → 5열로 떨어지는 문제 해결)
export const CARD_MIN_WIDTH_BY_DENSITY: Record<CardDensity, number> = {
  comfortable: 180,
  compact: 150,
};

// 기존 localStorage 데이터에 cardDensity 누락 가능 → DEFAULT와 머지하여 초기값 결정
const storedGridConfig = getItem<Partial<GridConfig>>(
  LOCAL_STORAGE_KEYS.todolistGridConfig,
  DEFAULT_GRID_CONFIG
);

export const gridConfigAtom = atomWithImprovedStorage<GridConfig>(
  LOCAL_STORAGE_KEYS.todolistGridConfig,
  { ...DEFAULT_GRID_CONFIG, ...storedGridConfig }
);

export const usePersistedGridConfig = () => useAtom(gridConfigAtom);

// 뷰포트 기반 자동 압축 판정. 일정 임계치 이하의 화면 폭에서는 하위 컴포넌트(HellKey/Cube 등)가
// 자동으로 압축 스타일(버튼 축소, 간격 축소)을 사용하도록.
const COMPACT_VIEWPORT_BREAKPOINT = 1100;

export const useIsCompact = () => {
  const getCurrent = () =>
    typeof window !== 'undefined' && window.innerWidth < COMPACT_VIEWPORT_BREAKPOINT;
  const [isCompact, setIsCompact] = useState<boolean>(getCurrent);

  useEffect(() => {
    const handle = () => setIsCompact(getCurrent());
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);

  return isCompact;
};

export default usePersistedGridConfig;
